// Post-processing: the Civ "painted diorama" finish, registered on the engine's post chain.
//
//   scene (MRT: colour, emissive, normal, depth)
//     ─► GTAO at half resolution: contact shadow under trees, peaks, cliffs (fades out when high)
//     ─► bloom on the emissive channel only: border ribbons, highlights, water glints
//     ─► tilt-shift depth of field around the camera target, close band only
//     ─► tone map + sRGB
//     ─► grade: shadows lifted toward a cool blue, highlights toward warm cream, a little saturation
//     ─► vignette ─► SMAA (med/high) or FXAA (low)
//
// Strengths follow the camera every frame (uniforms), so zooming out never turns the world view
// into a blur or a dark haze.

import {
  Fn, dot, float, mix, pow, sample, screenUV, smoothstep, uniform, unpackRGBToNormal, vec3, vec4,
} from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { ao } from 'three/examples/jsm/tsl/display/GTAONode.js';
import { bloom } from 'three/examples/jsm/tsl/display/BloomNode.js';
import { dof } from 'three/examples/jsm/tsl/display/DepthOfFieldNode.js';
import { fxaa } from 'three/examples/jsm/tsl/display/FXAANode.js';
import { smaa } from 'three/examples/jsm/tsl/display/SMAANode.js';
import type { Engine } from './engine.ts';
import { rgb } from './tsl/color.ts';

type V4 = Node<'vec4'>;

// Ambient occlusion: radius in world units follows the camera height. A hex is 1 unit from centre to
// corner, so the cap keeps the darkening inside the hex that casts it (a wider one smudges the
// neighbours of every hill and reads as a stain).
const AO_RADIUS = [0.25, 0.9] as const;
const AO_RADIUS_PER_HEIGHT = 0.012;
const AO_INTENSITY = 0.5;
const AO_FADE_HEIGHT = [120, 260] as const;
const AO_RESOLUTION = 0.5;
const AO_THICKNESS = 1.2;

// Bloom on emissive only (the channel is already selective, so the threshold stays low).
const BLOOM_STRENGTH = 0.4;
const BLOOM_RADIUS = 0.35;
const BLOOM_THRESHOLD = 0.02;

// Tilt-shift: sharp around the target, blurred toward the near and far edges, close band only.
const DOF_HEIGHT = [26, 14] as const; // off above the first height, full below the second
const DOF_BOKEH = 2.2;
const DOF_FOCAL_PER_DISTANCE = 0.55; // world units to full blur, per unit of focus distance

// Grade (sRGB, after tone mapping).
const GRADE = {
  shadow: '#1c2b40',
  highlight: '#fff1d6',
  lift: 0.16,
  gain: 0.22,
  saturation: 1.05,
  vignette: 0.25,
} as const;
const LUMA = vec3(0.2126, 0.7152, 0.0722);

export interface PostControls {
  /** Follow the camera: height above ground (hex radii) and distance to the target. */
  update(camHeight: number, focusDistance: number): void;
}

export function installPost(engine: Engine): PostControls {
  let aoPass: ReturnType<typeof ao> | null = null;
  const aoMix = uniform(AO_INTENSITY);
  const focus = uniform(30);
  const focal = uniform(15);
  const bokeh = uniform(0);

  engine.registerPost({
    name: 'gtao',
    order: 10,
    stage: 'hdr',
    tiers: ['med', 'high'],
    mrt: ['normal'],
    build(input, ctx) {
      const normalTex = ctx.channel('normal') as unknown as { sample(uv: Node<'vec2'>): V4 };
      const normal = sample((uv: Node<'vec2'>) => unpackRGBToNormal(normalTex.sample(uv).xyz));
      const pass = ao(ctx.scenePass.getTextureNode('depth'), normal, ctx.camera);
      pass.resolutionScale = AO_RESOLUTION;
      pass.thickness.value = AO_THICKNESS;
      aoPass = pass;
      const occlusion = (pass.getTextureNode() as unknown as V4).x;
      return vec4(input.xyz.mul(mix(float(1), occlusion, aoMix)), input.w);
    },
  });

  engine.registerPost({
    name: 'bloom',
    order: 20,
    stage: 'hdr',
    mrt: ['emissive'],
    build(input, ctx) {
      const glow = bloom(ctx.channel('emissive'), BLOOM_STRENGTH, BLOOM_RADIUS, BLOOM_THRESHOLD) as unknown as V4;
      return vec4(input.xyz.add(glow.xyz), input.w);
    },
  });

  engine.registerPost({
    name: 'tilt-shift',
    order: 30,
    stage: 'hdr',
    tiers: ['med', 'high'],
    build(input, ctx) {
      return dof(input, ctx.scenePass.getViewZNode(), focus, focal, bokeh) as unknown as V4;
    },
  });

  engine.registerPost({
    name: 'grade',
    order: 10,
    stage: 'ldr',
    build(input) {
      return grade(input);
    },
  });

  engine.registerPost({
    name: 'smaa',
    order: 90,
    stage: 'ldr',
    tiers: ['med', 'high'],
    build(input) {
      return smaa(input) as unknown as V4;
    },
  });

  engine.registerPost({
    name: 'fxaa',
    order: 90,
    stage: 'ldr',
    tiers: ['low'],
    build(input) {
      return fxaa(input) as unknown as V4;
    },
  });

  return {
    update(h, distance) {
      if (aoPass) {
        aoPass.radius.value = Math.min(AO_RADIUS[1], Math.max(AO_RADIUS[0], h * AO_RADIUS_PER_HEIGHT));
      }
      aoMix.value = AO_INTENSITY * (1 - smoothstepJs(AO_FADE_HEIGHT[0], AO_FADE_HEIGHT[1], h));
      focus.value = distance;
      focal.value = distance * DOF_FOCAL_PER_DISTANCE;
      bokeh.value = DOF_BOKEH * smoothstepJs(DOF_HEIGHT[0], DOF_HEIGHT[1], h);
    },
  };
}

/** Lift, gain, saturation and vignette on the sRGB image. */
const grade = Fn(([input]: [V4]) => {
  let c = input.xyz;
  const luma = dot(c, LUMA);
  c = mix(c, rgb(GRADE.shadow), pow(float(1).sub(luma), 3).mul(GRADE.lift));
  c = c.mul(mix(vec3(1), rgb(GRADE.highlight), luma.mul(luma).mul(GRADE.gain)));
  c = mix(vec3(dot(c, LUMA)), c, GRADE.saturation);
  const edge = screenUV.sub(0.5).length().mul(1.25);
  c = c.mul(float(1).sub(smoothstep(0.35, 0.95, edge).mul(GRADE.vignette)));
  return vec4(c, input.w);
}) as unknown as (input: V4) => V4;

function smoothstepJs(e0: number, e1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}
